import { t, useLocale } from "@/i18n/locale";
import { Message } from "@/i18n/Message";
import { User } from "lucide-react";
import type { DiscoverDeveloper } from "../discoverClient";
import SourceLink from "../source/SourceLink";
import type { AppSourcePresentation } from "../types";
import type { DiscoverySelection } from "./types";

/** A publisher is never presented as the upstream author. */
export default function DeveloperAttribution({
  developers,
  onNavigate,
  compact = false,
  source,
  sourceTarget,
}: {
  developers: DiscoverDeveloper[];
  source?: AppSourcePresentation;
  sourceTarget?: DiscoverySelection;
  onNavigate?: (route: string) => void;
  compact?: boolean;
}) {
  useLocale();
  const authors = developers.filter(
    (developer) => developer.role !== "publisher",
  );
  const limit = 1;
  const repository = authors.find(
    (author) => author.role === "repository_owner",
  )?.repository_url;
  const contributorsUrl = (() => {
    if (!repository) return null;
    try {
      const url = new URL(repository);
      const parts = url.pathname.split("/").filter(Boolean);
      return url.protocol === "https:" &&
        url.hostname === "github.com" &&
        !url.username &&
        !url.password &&
        !url.search &&
        !url.hash &&
        parts.length === 2
        ? `${url.origin}/${parts.join("/")}/graphs/contributors`
        : null;
    } catch {
      return null;
    }
  })();
  const hasSourceLink =
    !compact && sourceTarget && source?.visibility !== "private";
  const link = (developer: DiscoverDeveloper) => (
    <span key={developer.id} className="discovery-attribution-group">
      {/* Even the compact card names the relationship: the review found
          "contributor" figures with no visible meaning. */}
      <span className="discovery-attribution-label">
        {developer.role === "modifier"
          ? t("Modified by")
          : developer.role === "repository_owner"
            ? hasSourceLink
              ? t("by")
              : t("Source by")
            : t("By")}
      </span>
      <a
        className="discovery-developer"
        href={developer.profile_url}
        title={
          developer.role === "modifier"
            ? t("Code modified by {{value0}}", { value0: developer.name })
            : developer.role === "repository_owner"
              ? t("Source project: {{value0}}", { value0: developer.name })
              : t("Code author: {{value0}}", { value0: developer.name })
        }
        {...(developer.profile_url.startsWith("/")
          ? {
              onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
                if (
                  onNavigate &&
                  event.button === 0 &&
                  !event.metaKey &&
                  !event.ctrlKey &&
                  !event.shiftKey &&
                  !event.altKey
                ) {
                  event.preventDefault();
                  onNavigate(developer.profile_url);
                }
              },
            }
          : { target: "_blank", rel: "noopener noreferrer" })}
      >
        {developer.avatar_url ? (
          <img src={developer.avatar_url} alt="" loading="lazy" />
        ) : (
          <User size={compact ? 18 : 22} aria-hidden="true" />
        )}
        <span>{developer.name}</span>
      </a>
    </span>
  );
  return (
    <div
      className={`discovery-developers${compact ? " discovery-card-developers" : ""}`}
    >
      {hasSourceLink ? (
        <SourceLink
          source={source}
          target={sourceTarget}
          onNavigate={onNavigate}
          className="discovery-attribution-source"
        />
      ) : null}
      {authors.length ? (
        authors.slice(0, limit).map(link)
      ) : (
        <span className="discovery-author-unavailable"><Message message={"Author unavailable"} /></span>
      )}
      {authors.length > limit || contributorsUrl ? (
        <details className="discovery-developers-more">
          <summary
            aria-label={
              authors.length > limit
                ? t("View {{value0}} additional code contributors", { value0: authors.length - limit })
                : t("View repository contributors")
            }
          >
            {compact ? (
              <span className="discovery-avatar-stack" aria-hidden="true">
                {authors
                  .slice(1, 3)
                  .map((d) =>
                    d.avatar_url ? (
                      <img key={d.id} src={d.avatar_url} alt="" />
                    ) : null,
                  )}
              </span>
            ) : null}
            {authors.length > limit ? t("+{{value0}} contributors", { value0: authors.length - limit }) : t("Contributors")}
          </summary>
          <div>
            {authors.slice(limit).map(link)}
            {contributorsUrl ? (
              <a
                href={contributorsUrl}
                target="_blank"
                rel="noopener noreferrer"
              ><Message message={"Repository contributors"} /></a>
            ) : null}
          </div>
        </details>
      ) : null}
    </div>
  );
}
